import React, { useState, useEffect } from "react";
import {
  FiHeart,
  FiTrash2,
  FiEye,
  FiCalendar,
  FiChevronRight,
  FiShoppingCart
} from "react-icons/fi";
import axios from "axios";
import { baseurl, imgurl } from "../baseurl";
import { Link, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";

function MyWishlist() {
  const [wishlist, setWishlist] = useState([]);
  const [loading, setLoading] = useState(true);
  const customerId = localStorage.getItem("customerId");
  const navigate = useNavigate();

  const Toast = Swal.mixin({
    toast: true,
    position: "top-end",
    showConfirmButton: false,
    timer: 3000,
    timerProgressBar: true,
  });

  useEffect(() => {
    if (!customerId) {
      setLoading(false);
      return;
    }
    fetchWishlist();
  }, [customerId]);

  const fetchWishlist = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${baseurl}getWishlistByCustomer/${customerId}`);
      if (data.success) {
        setWishlist(data.wishlist);
      }
    } catch (err) {
      console.error("Error fetching wishlist:", err);
    } finally {
      setLoading(false);
    }
  };

  const confirmRemove = (e, productId, productName) => {
    e.preventDefault(); // Prevent navigation if clicking inside a link area
    Swal.fire({
      title: "Remove Item?",
      text: `Are you sure you want to remove this item from your wishlist?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#2874f0", 
      cancelButtonColor: "#fb641b",  
      confirmButtonText: "Yes, Remove",
      cancelButtonText: "Cancel",
      reverseButtons: true,
      customClass: { popup: 'flipkart-rounded-modal' }
    }).then((result) => {
      if (result.isConfirmed) {
        handleRemove(productId);
      }
    });
  };

  const handleRemove = async (productId) => {
    try {
      const { data } = await axios.delete(`${baseurl}removeFromWishlist`, {
        data: { customerId, productId }
      });
      if (data.success) {
        setWishlist((prev) => prev.filter((item) => item.product_id !== productId));
        Toast.fire({ icon: "success", title: "Removed from wishlist" });
      }
    } catch (err) {
      setWishlist((prev) => prev.filter((item) => item.product_id !== productId));
      Toast.fire({ icon: "info", title: "Item removed" });
    }
  };

  const calculateDiscount = (mrp, price) => {
    if (!mrp || mrp <= price) return null;
    return Math.round(((mrp - price) / mrp) * 100);
  };

  return (
    <div className="wishlist-page-container">
      {/* Flipkart Header Section */}
      <div className="wishlist-header shadow-sm sticky-top bg-white">
        <div className="container py-3 d-flex justify-content-between align-items-center">
          <h5 className="fw-bold mb-0">My Wishlist ({wishlist.length})</h5>
          <Link to="/" className="text-primary text-decoration-none fw-medium small">
            Continue Shopping
          </Link>
        </div>
      </div>

      <div className="container py-4">
        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status"></div>
          </div>
        ) : wishlist.length === 0 ? (
          <div className="empty-wishlist text-center py-5 shadow-sm bg-white rounded">
            <img src="https://rukminim1.flixcart.com/www/800/800/promos/16/05/2019/d405a710-21da-4f32-9022-15fbfaae6666.png?q=90" alt="Empty" style={{width: '180px'}} className="mb-4" />
            <h5 className="fw-bold">Your wishlist is empty!</h5>
            <p className="text-muted">Explore our top categories and add some items.</p>
            <Link to="/" className="btn btn-primary px-5 py-2 mt-2">Shop Now</Link>
          </div>
        ) : (
          <div className="wishlist-list bg-white shadow-sm rounded">
            {wishlist.map((item, index) => (
              <div key={item.wishlist_id} className={`wishlist-item ${index !== wishlist.length -1 ? 'border-bottom' : ''}`}>
                <div className="row g-0 p-3 p-md-4 align-items-center">
                  
                  {/* Image Column */}
                  <div className="col-4 col-md-2 text-center">
                    <Link to={`/product/${item.slug}`} className="item-img-box">
                       <img src={`${imgurl}${item.featured_image}`} alt={item.name} />
                    </Link>
                  </div>

                  {/* Details Column */}
                  <div className="col-8 col-md-6 ps-3 ps-md-4">
                    <Link to={`/product/${item.slug}`} className="text-decoration-none">
                        <h6 className="product-title text-truncate-2">{item.name}</h6>
                    </Link>
                    
                    {/* <div className="rating-row mb-1">
                       <span className="badge bg-success small px-2">4.2 ★</span>
                       <span className="text-muted small ms-2 d-none d-sm-inline">(2,450)</span>
                    </div> */}

                    <div className="price-row d-flex align-items-center gap-2 flex-wrap mb-1">
                      <span className="price-current">₹{Number(item.price).toLocaleString()}</span>
                      {item.mrp > item.price && (
                        <>
                          <span className="price-old">₹{Number(item.mrp).toLocaleString()}</span>
                          <span className="price-off">{calculateDiscount(item.mrp, item.price)}% off</span>
                        </>
                      )}
                    </div>

                    <div className="stock-info">
                       {item.stock > 0 ? (
                         <span className="text-success small fw-bold">In Stock</span>
                       ) : (
                         <span className="text-danger small fw-bold">Out of Stock</span>
                       )}
                    </div>
                  </div>

                  {/* Desktop Actions Column */}
                  <div className="col-md-4 d-none d-md-flex flex-column gap-2 align-items-end pe-3">
                     <button 
                        className="btn btn-primary btn-sm w-75 py-2 fw-bold d-flex align-items-center justify-content-center gap-2"
                        onClick={() => navigate(`/product/${item.slug}`)}
                     >
                        <FiEye /> VIEW DETAILS
                     </button>
                     <button 
                        className="btn btn-outline-secondary btn-sm w-75 py-2 fw-bold d-flex align-items-center justify-content-center gap-2"
                        onClick={(e) => confirmRemove(e, item.product_id, item.name)}
                     >
                        <FiTrash2 /> REMOVE
                     </button>
                  </div>

                  {/* Mobile Actions Bar */}
                  <div className="col-12 d-md-none mt-3 border-top pt-2 d-flex">
                     <button 
                        className="flex-fill btn btn-link text-danger text-decoration-none fw-bold small border-end py-2"
                        onClick={(e) => confirmRemove(e, item.product_id, item.name)}
                     >
                        <FiTrash2 className="me-1" /> REMOVE
                     </button>
                     <button 
                        className="flex-fill btn btn-link text-primary text-decoration-none fw-bold small py-2"
                        onClick={() => navigate(`/product/${item.slug}`)}
                     >
                        <FiEye className="me-1" /> DETAILS
                     </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <style>{`
        .wishlist-page-container { background-color: #f1f3f6; min-height: 100vh; font-family: Roboto, Arial, sans-serif; }
        .wishlist-header { border-bottom: 1px solid #e0e0e0; position: sticky; top: 0; z-index: 100; }
        .wishlist-list { border-radius: 4px; overflow: hidden; }
        
        .wishlist-item { transition: background 0.2s; }
        .wishlist-item:hover { background: #fdfdfd; }

        .item-img-box { width: 100%; height: 110px; display: flex; align-items: center; justify-content: center; overflow: hidden; }
        .item-img-box img { max-height: 100%; max-width: 100%; object-fit: contain; }

        .product-title { font-size: 15px; color: #212121; margin-bottom: 4px; line-height: 1.4; font-weight: 500; }
        .text-truncate-2 { display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }

        .price-current { font-size: 18px; font-weight: bold; color: #212121; }
        .price-old { color: #878787; text-decoration: line-through; font-size: 13px; }
        .price-off { color: #388e3c; font-size: 13px; font-weight: bold; }

        .flipkart-rounded-modal { border-radius: 4px !important; font-size: 14px; }
        
        .btn-primary { background-color: #fb641b; border: none; border-radius: 2px; } /* Flipkart Orange for Buy/View */
        .btn-primary:hover { background-color: #f4511e; }
        
        .btn-outline-secondary { border-color: #e0e0e0; color: #212121; border-radius: 2px; }
        .btn-outline-secondary:hover { background-color: #f9f9f9; border-color: #d0d0d0; color: #000; }

        @media (max-width: 768px) {
          .product-title { font-size: 14px; }
          .price-current { font-size: 16px; }
          .item-img-box { height: 90px; }
        }
      `}</style>
    </div>
  );
}

export default MyWishlist;